import { useCallback, useEffect, useState } from 'react';
import { showNotification } from '@mantine/notifications';
import { suggestedModulesService } from '@/services/suggested-modules.service';
import { type SuggestedModule, type SuggestedModuleCreateRequest } from '@/types/suggestedModules';
import { showError } from '@/shared/utils/errors';

export const useSuggestedModules = () => {
  const [items, setItems] = useState<SuggestedModule[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchModules = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await suggestedModulesService.getSuggestedModules();
      const { items: newItems, total: totalItems } = response.data;

      setItems(newItems || []);
      setTotal(totalItems || 0);

      if (newItems && newItems.length > 0) {
        setCursor(newItems[newItems.length - 1].id);
      } else {
        setCursor(undefined);
      }
    } catch (error) {
      console.error(error);
      showError(error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (items.length >= total || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await suggestedModulesService.getSuggestedModules(cursor);
      const { items: newItems } = response.data;

      if (newItems && newItems.length > 0) {
        setItems((prev) => [...prev, ...newItems]);
        setCursor(newItems[newItems.length - 1].id);
      }
    } catch (error) {
      console.error(error);
      showError(error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [cursor, isLoadingMore, items.length, total]);

  const createModule = async (data: SuggestedModuleCreateRequest) => {
    setIsSaving(true);
    try {
      await suggestedModulesService.createSuggestedModule(data);
      showNotification({
        title: 'Success',
        message: 'Module created successfully',
        color: 'green',
      });
      fetchModules();
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const updateModule = async (id: string, data: SuggestedModuleCreateRequest) => {
    setIsSaving(true);
    try {
      await suggestedModulesService.updateSuggestedModule(id, data);
      showNotification({
        title: 'Success',
        message: 'Module updated successfully',
        color: 'green',
      });
      fetchModules();
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const deleteModule = async (id: string) => {
    setIsDeleting(true);
    try {
      await suggestedModulesService.deleteSuggestedModule(id);
      showNotification({
        title: 'Success',
        message: 'Module deleted successfully',
        color: 'green',
      });
      fetchModules();
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  return {
    items,
    total,
    isLoading,
    isLoadingMore,
    hasMore: items.length < total,
    isSaving,
    isDeleting,
    handleLoadMore,
    createModule,
    updateModule,
    deleteModule,
    refresh: fetchModules,
  };
};
